import { forwardRef, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import clsx from 'clsx';
import type { LucideIcon } from 'lucide-react';

interface FieldWrapperProps {
  label?: string;
  error?: string | null;
  /** Gelber Hinweis (statt roter Fehler) für "hat einen Wert, aber unsicher/unbestätigt" - z.B. ein OCR-Vorschlag ohne Stammdaten-Treffer. */
  warning?: string | null;
  hint?: string;
  children: ReactNode;
  htmlFor?: string;
}

export function FieldWrapper({ label, error, warning, hint, children, htmlFor }: FieldWrapperProps) {
  return (
    <label htmlFor={htmlFor} className="flex flex-col gap-1.5">
      {label && <span className="text-sm font-medium text-ivory-300">{label}</span>}
      {children}
      {error ? (
        <span className="text-xs text-danger-400">{error}</span>
      ) : warning ? (
        <span className="text-xs text-warning-400">{warning}</span>
      ) : hint ? (
        <span className="text-xs text-ivory-500">{hint}</span>
      ) : null}
    </label>
  );
}

const fieldBaseClasses =
  'w-full rounded-md border px-3 py-2 text-sm text-ivory-100 placeholder:text-ivory-500 outline-none transition-colors focus:border-accent-400 focus:ring-2 focus:ring-accent-500/20 disabled:opacity-50';

/** Rot = Pflichtfeld fehlt/ungültig, Gelb = Wert vorhanden aber unbestätigt (z.B. OCR-Rohtext ohne Stammdaten-Treffer), sonst neutral. Echte Hintergrundfarbe ("hinterlegt"), nicht nur ein Rahmen - soll auf einen Blick auffallen. */
export function fieldToneClasses(error?: string | null | boolean, warning?: string | null | boolean): string {
  if (error) return 'border-danger-500 bg-danger-500/10';
  if (warning) return 'border-warning-500 bg-warning-500/10';
  return 'border-surface-600 bg-surface-900';
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string | null;
  warning?: string | null;
  hint?: string;
  icon?: LucideIcon;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input({ label, error, warning, hint, id, className, icon: Icon, ...rest }, ref) {
  return (
    <FieldWrapper label={label} error={error} warning={warning} hint={hint} htmlFor={id}>
      {Icon ? (
        <div className="relative">
          <Icon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ivory-500" strokeWidth={1.75} />
          <input ref={ref} id={id} className={clsx(fieldBaseClasses, 'pl-9', fieldToneClasses(error, warning), className)} {...rest} />
        </div>
      ) : (
        <input ref={ref} id={id} className={clsx(fieldBaseClasses, fieldToneClasses(error, warning), className)} {...rest} />
      )}
    </FieldWrapper>
  );
});

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string | null;
  warning?: string | null;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea({ label, error, warning, id, className, ...rest }, ref) {
  return (
    <FieldWrapper label={label} error={error} warning={warning} htmlFor={id}>
      <textarea ref={ref} id={id} className={clsx(fieldBaseClasses, 'min-h-20', fieldToneClasses(error, warning), className)} {...rest} />
    </FieldWrapper>
  );
});

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string | null;
  warning?: string | null;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select({ label, error, warning, id, className, children, ...rest }, ref) {
  return (
    <FieldWrapper label={label} error={error} warning={warning} htmlFor={id}>
      <select ref={ref} id={id} className={clsx(fieldBaseClasses, fieldToneClasses(error, warning), className)} {...rest}>
        {children}
      </select>
    </FieldWrapper>
  );
});
